import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Send, AlertCircle, CheckCircle, Lightbulb, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { AppConfig } from "@/datasources/AppConfig";
import { reportarProblemaService } from "@/services/reportarProblemaService";

export default function ReportarProblemaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const [problema, setProblema] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Obter a página de origem da URL query parameter
  const searchParams = new URLSearchParams(location.search);
  const origemPath = searchParams.get('origem');
  const isGeneralReport = !origemPath;

  // Identificação da tela de origem (apenas se não for relatório geral)
  const getOrigemScreenId = () => {
    if (!origemPath) return 'GERAL';
    // Fallback mapping since AppConfig.getScreenId might not be available
    const screenMap: Record<string, string> = {
      '/': 'DASHBOARD',
      '/solicitacoes': 'SOLICITACOES',
      '/agentes': 'AGENTES',
      '/ouvidoria': 'OUVIDORIA',
      '/ouvidorias-supervisionadas': 'OUVIDORIAS_SUPERVISIONADAS',
      '/supervisora': 'SUPERVISORA',
      '/topicos': 'TOPICOS',
      '/relatorios': 'RELATORIOS',
      '/configuracoes': 'CONFIGURACOES',
      '/perfil': 'PERFIL',
    };
    const normalizedPath = origemPath.replace(/\/\d+/g, '/:id');
    return screenMap[normalizedPath] || 'UNKNOWN';
  };

  const getOrigemScreenName = () => {
    if (!origemPath) return 'Relatório Geral';
    
    const screenNames: Record<string, string> = {
      "/": "Visão Geral",
      "/solicitacoes": "Solicitações",
      "/agentes": "Agentes",
      "/ouvidoria": "Ouvidoria",
      "/ouvidorias-supervisionadas": "Ouvidorias Supervisionadas",
      "/supervisora": "Supervisora",
      "/topicos": "Tópicos",
      "/relatorios": "Relatórios",
      "/configuracoes": "Configurações",
      "/perfil": "Perfil",
    };
    
    // Para rotas com ID, extrai o base
    const basePath = origemPath.split('/')[1];
    const screenName = screenNames[`/${basePath}`];
    
    if (screenName) {
      return screenName;
    }
    
    // Fallback para rotas não mapeadas
    if (origemPath === '/') return 'Visão Geral';
    return origemPath.slice(1).charAt(0).toUpperCase() + origemPath.slice(2);
  };

  const origemScreenId = getOrigemScreenId();
  const origemScreenName = getOrigemScreenName();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!problema.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "Por favor, descreva o problema encontrado.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await reportarProblemaService.reportarProblema({
        problema: problema.trim(),
        indentificacaoTela: origemScreenId,
      });

      if ('failure' in result) {
        const errorMessage = result.failure.message || "Ocorreu um erro ao tentar enviar seu relatório. Tente novamente.";
        
        toast({
          title: "Erro ao enviar relatório",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        setIsSubmitted(true);
        toast({
          title: "Excelente escolha! 🎉",
          description: "Você acabou de economizar tempo precioso! Enquanto outros ficam na fila do telefone, seu problema já está sendo analisado pela nossa equipe técnica. Em poucas horas, pode estar resolvido. Você entendeu o segredo do suporte eficiente! 🚀",
          duration: 6000,
        });
      }

    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : "Ocorreu um erro inesperado. Tente novamente.";
      
      toast({
        title: "Erro ao enviar relatório",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 p-4">
        <div className="max-w-2xl mx-auto pt-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>

          <Card className="text-center py-12">
            <CardContent className="space-y-6">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              
              <div>
                <h1 className="text-2xl font-bold text-green-800 dark:text-green-200 mb-2">
                  Relatório Enviado com Sucesso!
                </h1>
                <p className="text-muted-foreground">
                  Obrigado por nos ajudar a melhorar o sistema UrbNet.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 text-left max-w-md mx-auto">
                <h3 className="font-medium mb-2">Resumo do relatório:</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Tela: {origemScreenName}</li>
                  <li>• Identificação: {origemScreenId}</li>
                  <li>• Status: Enviado para análise</li>
                </ul>
              </div>

              <div className="flex gap-3 justify-center">
                <Button onClick={() => navigate(-1)} variant="outline">
                  Voltar à página anterior
                </Button>
                <Button onClick={() => navigate("/")}>
                  Ir para o Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 p-4">
      <div className="max-w-2xl mx-auto pt-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        <Card className="mb-6 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20 border-orange-200 dark:border-orange-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground">
                  Prefira reportar aqui antes de ligar! 📞
                </h3>
                <p className="text-muted-foreground">
                  Nossa equipe técnica resolve problemas muito mais rápido por aqui do que por telefone. 
                  Sem esperas, sem transferências, sem "já resolvi seu problema" quando não resolveu! 
                </p>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/saiba-mais-reportar')}
              className="text-orange-600 border-orange-200 hover:bg-orange-50 dark:text-orange-400 dark:border-orange-800 dark:hover:bg-orange-950/20"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Por que reportar aqui é melhor?
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Detalhes do Problema</CardTitle>
                <CardDescription>
                  Descreva em detalhes o problema que você encontrou
                </CardDescription>
              </div>
              <Badge variant="secondary" className="text-xs">
                {origemScreenName}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <Lightbulb className="h-4 w-4" />
              <AlertDescription>
                <strong>Como funciona nosso suporte:</strong> Não temos uma equipe de suporte tradicional. 
                Em vez disso, temos uma equipe de desenvolvedores que monitora diretamente todos os relatórios de problemas. 
                Isso significa que seu problema é analisado por quem realmente entende do sistema e pode resolvê-lo rapidamente. 
                Quanto mais detalhes você fornecer, mais rápido poderemos identificar e corrigir o problema.
              </AlertDescription>
            </Alert>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="problema">Descrição do Problema *</Label>
                <Textarea
                  id="problema"
                  placeholder="Descreva detalhadamente o problema encontrado...
Ex: Ao tentar aprovar uma solicitação, o sistema apresenta uma mensagem de erro e não permite concluir a ação."
                  value={problema}
                  onChange={(e) => setProblema(e.target.value)}
                  rows={6}
                  className="resize-none"
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Seja o mais específico possível. Descreva o que você tentou fazer, o que aconteceu e o que esperava que acontecesse.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !problema.trim()}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Enviar Relatório
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Card de incentivo final */}
        <Card className="mt-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
              <p className="font-medium text-green-800 dark:text-green-200">
                Você evitou a dor de cabeça do suporte por telefone! 📞✅
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
